import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

interface DeliveryMapProps {
  restaurantLat: number;
  restaurantLng: number;
  radiusKm: number;
  customerLat?: number | null;
  customerLng?: number | null;
  primaryColor?: string;
}

const DeliveryMap = ({ restaurantLat, restaurantLng, radiusKm, customerLat, customerLng, primaryColor = "#e60023" }: DeliveryMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    // Clean up previous instance
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const map = L.map(mapRef.current, {
      zoomControl: false,
      attributionControl: false,
      dragging: true,
      scrollWheelZoom: false,
    }).setView([restaurantLat, restaurantLng], 13);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
    }).addTo(map);

    // Restaurant marker
    const restaurantIcon = L.divIcon({
      html: `<div style="background:${primaryColor};width:28px;height:28px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
      </div>`,
      className: "",
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });

    L.marker([restaurantLat, restaurantLng], { icon: restaurantIcon }).addTo(map);

    // Delivery radius circle
    L.circle([restaurantLat, restaurantLng], {
      radius: radiusKm * 1000,
      color: primaryColor,
      fillColor: primaryColor,
      fillOpacity: 0.08,
      weight: 2,
      dashArray: "6 4",
    }).addTo(map);

    // Customer marker
    if (customerLat && customerLng) {
      const customerIcon = L.divIcon({
        html: `<div style="background:#22c55e;width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        </div>`,
        className: "",
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });
      L.marker([customerLat, customerLng], { icon: customerIcon }).addTo(map);

      // Fit bounds to show both markers
      const bounds = L.latLngBounds(
        [restaurantLat, restaurantLng],
        [customerLat, customerLng]
      ).pad(0.3);
      map.fitBounds(bounds);
    } else {
      // Fit to radius
      const bounds = L.latLngBounds(
        [restaurantLat - radiusKm * 0.009, restaurantLng - radiusKm * 0.012],
        [restaurantLat + radiusKm * 0.009, restaurantLng + radiusKm * 0.012]
      );
      map.fitBounds(bounds);
    }

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [restaurantLat, restaurantLng, radiusKm, customerLat, customerLng, primaryColor]);

  return (
    <div
      ref={mapRef}
      className="w-full rounded-xl overflow-hidden border border-[hsl(0,0%,90%)]"
      style={{ height: 180 }}
    />
  );
};

export default DeliveryMap;
